import {
  ReservationInterface,
  ReservationListInterface,
} from "../slices/reservationSlice";
import { store } from "../store";

import { baseApi } from "./apiSingleton";

export interface IInitReservationListRequest {
  channel: string;
  channel_partner: string;
  agency: string;
  ship_code?: string;
  sailing_code?: string;
  lastname?: string;
  embarkation_from?: string;
  embarkation_to?: string;
}

interface ConfirmReservationRequest {
  pnr: string;
  transactions: Array<{
    transaction_type: "creditcard";
    made_on?: string;
    made_by?: "Website";
    amount?: string;
    currency?: string;
    card_clearance: {
      clearance_system: "airwallex" | "stripe";
      auth_code: string;
      transaction_id: string;
      other: {
        card_type: string;
        last_four: string;
        transaction_id: string;
        intent_id: string;
      };
    };
  }>;
}

export interface AvailableGrade {
  code: string;
  name: Record<string, string>;
  meta_category: string;
  total_price: number;
  available_cabins: number;
}

export interface AvailableCabin {
  bed_config: any[];
  cabin_attributes: any[];
  cabin_measurement: string;
  cabin_size: string;
  deck_code: string;
  deck_name: string;
  location: string;
  max_occupancy: string;
  min_occupancy: string;
  number: string;
  position: string;
}

export type ReservationListResponse = ReservationListInterface[];

export const ReservationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    cancelReservation: builder.mutation<void, { pnr: string }>({
      query: ({ pnr }) => {
        const { agency: channel_partner, agent: agency } =
          store.getState().session;

        return {
          url: `/res/reservation/${pnr}/`,
          method: "DELETE",
          body: {
            channel: "CS",
            channel_partner,
            agency,
          },
        };
      },
    }),

    initReservationList: builder.query<
      ReservationListResponse,
      IInitReservationListRequest
    >({
      query: (params) => {
        const { api_language } = store.getState().environment;

        return {
          url: `res/reservation-list/?language=${api_language}`,
          method: "GET",
          params,
        };
      },
    }),

    retrieveReservation: builder.query<
      ReservationInterface,
      { pnr: string; channel_partner: string; agency: string }
    >({
      query: (params) => {
        const { api_language } = store.getState().environment;

        return {
          url: `/res/reservation-list/${params.pnr}/?channel=CS&channel_partner=${params.channel_partner}&agency=${params.agency}&language=${api_language}`,
          method: "GET",
        };
      },
    }),

    confirmReservation: builder.query<null, ConfirmReservationRequest>({
      query: (request) => {
        const { agency: channel_partner, agent: agency } =
          store.getState().session;
        const { api_language } = store.getState().environment;

        const { pnr, ...body } = request;

        return {
          url: `/res/reservation/${pnr}/?language=${api_language}`,
          method: "PUT",
          body: {
            channel: "CS",
            channel_partner,
            agency,
            ...body,
          },
        };
      },
    }),

    confirmPayment: builder.query<null, ConfirmReservationRequest>({
      query: (request) => {
        const { agency: channel_partner, agent: agency } =
          store.getState().session;
        const { api_language } = store.getState().environment;

        const { pnr, ...body } = request;

        return {
          url: `/res/payment/${pnr}/?language=${api_language}`,
          method: "PUT",
          body: {
            channel: "CS",
            channel_partner,
            agency,
            ...body,
          },
        };
      },
    }),

    retrieveAvailableGrades: builder.query<
      Record<"grades", AvailableGrade[]>,
      Record<string, string>
    >({
      query: (params) => {
        return {
          url: `/avl/available-grades/?${new URLSearchParams(params)}`,
          method: "GET",
        };
      },
    }),

    retrieveAvailableCabins: builder.query<
      AvailableCabin[],
      Record<string, string>
    >({
      query: (params) => {
        return {
          url: `/avl/available-cabins/?${new URLSearchParams(params)}`,
          method: "GET",
        };
      },
    }),

    holdNewCabin: builder.query<unknown, Record<string, any>>({
      query: (body) => {
        return {
          url: "/avl/hold-cabin/",
          method: "PUT",
          body,
        };
      },
    }),

    updateNewCabin: builder.query<
      unknown,
      {
        cabinUuid: string;
        sailing_uuid: string;
        pnr: string;
        channel: string;
        channel_partner: string;
        agency: string;
        rate_code: string;
        cabin: {
          uuid: string;
          grade_code: string;
          cabin_number: string;
          guests: string;
        };
        reprice: boolean;
        commit: boolean;
      }
    >({
      query: ({ cabinUuid, ...body }) => {
        return {
          url: `/res/cabin/${cabinUuid}/`,
          method: "PATCH",
          body,
        };
      },
    }),
  }),
});

export const {
  useLazyInitReservationListQuery,
  useLazyRetrieveReservationQuery,
  useLazyConfirmReservationQuery,
  useLazyConfirmPaymentQuery,
  useLazyRetrieveAvailableGradesQuery,
  useLazyRetrieveAvailableCabinsQuery,
  useLazyHoldNewCabinQuery,
  useLazyUpdateNewCabinQuery,
  useCancelReservationMutation,
} = ReservationApi;
